<script setup lang="ts">
    import { computed } from 'vue'

    /**
     * The user pointing at the page that embeds the widget (spec
     * `page-point-and-choose.md`, P): the bar that says what to do, the box on
     * the element that would be chosen, and the bubble next to a text the user
     * selected. Fixed on the page, next to the chat and not inside it, as the
     * page control bar: a transformed ancestor would pin it to itself. While
     * the user points, a clear layer over the whole window takes the pointer,
     * so no event of the gesture reaches the site, whatever it listens to; the
     * widget finds what lies under the layer at each point.
     */
    const props = defineProps<{
        /** The user is choosing an element: the bar and the box show. */
        picking: boolean
        /** The element under the pointer, where the box goes, and its name. */
        box?: {
            top: number
            left: number
            width: number
            height: number
            label: string
        }
        /** Where the bubble of a selected text goes. */
        bubble?: { top: number; left: number }
    }>()

    const emit = defineEmits<{
        cancel: []
        ask: []
        /** The pointer over the layer, at this point of the window. */
        move: [x: number, y: number]
        /** A press on the layer, at this point of the window: it chooses. */
        press: [x: number, y: number]
    }>()

    const px = (value: number) => `${Math.round(value)}px`

    const boxStyle = computed(() =>
        props.box
            ? {
                  top: px(props.box.top),
                  left: px(props.box.left),
                  width: px(props.box.width),
                  height: px(props.box.height),
              }
            : undefined,
    )

    const bubbleStyle = computed(() =>
        props.bubble
            ? { top: px(props.bubble.top), left: px(props.bubble.left) }
            : undefined,
    )
</script>

<template>
    <!-- One root, with no box of its own: the widget's color reaches the
         box and the bubble through it -->
    <div class="pk-page-pointer">
        <div
            v-if="picking"
            class="pk-page-pointer__layer"
            aria-hidden="true"
            @pointermove="emit('move', $event.clientX, $event.clientY)"
            @pointerdown.prevent="emit('move', $event.clientX, $event.clientY)"
            @click.prevent="emit('press', $event.clientX, $event.clientY)"
            @contextmenu.prevent />
        <Transition name="pk-page-pointer-bar" appear>
            <div v-if="picking" class="pk-page-pointer__bar" role="status">
                <VvIcon
                    name="ri:focus-3-line"
                    class="pk-page-pointer__bar-icon" />
                <span class="pk-page-pointer__text">
                    {{ $t('pagePointer.hint') }}
                </span>
                <button
                    type="button"
                    class="pk-page-pointer__cancel"
                    @click="emit('cancel')">
                    {{ $t('action.cancel') }}
                </button>
            </div>
        </Transition>
        <div
            v-if="picking && box"
            class="pk-page-pointer__box"
            :style="boxStyle"
            aria-hidden="true">
            <span
                v-if="box.label"
                class="pk-page-pointer__label"
                :class="{ 'pk-page-pointer__label--inside': box.top < 28 }">
                {{ box.label }}
            </span>
        </div>
        <Transition name="pk-page-pointer-ask">
            <button
                v-if="bubble && !picking"
                type="button"
                class="pk-page-pointer__ask"
                :style="bubbleStyle"
                @pointerdown.prevent
                @mousedown.prevent
                @click="emit('ask')">
                <VvIcon
                    name="ri:chat-quote-line"
                    class="pk-page-pointer__ask-icon" />
                {{ $t('pagePointer.ask') }}
            </button>
        </Transition>
    </div>
</template>

<style lang="scss">
    .pk-page-pointer {
        // The agent's color where the widget sets it, the brand otherwise
        --pk-page-pointer-color: var(
            --pk-agent-presence-color,
            var(--color-brand)
        );

        display: contents;

        // Over the whole window, under the box and the bar: it takes the
        // pointer in place of the page
        &__layer {
            position: fixed;
            inset: 0;
            z-index: 2147483645;
            cursor: crosshair;
        }

        &__bar {
            position: fixed;
            top: var(--spacing-48);
            left: 50%;
            transform: translateX(-50%);

            // Above the site's own layers, whatever they are
            z-index: 2147483647;
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            max-width: min(var(--spacing-384), calc(100vw - var(--spacing-32)));
            padding: var(--spacing-8) var(--spacing-8) var(--spacing-8)
                var(--spacing-14);
            border-radius: var(--rounded-full);

            // As the page control bar: the theme's colors swapped, readable on
            // any site
            background-color: var(--color-word-1);
            color: var(--color-surface);
            font-family: var(--font-sans);
            font-size: var(--text-14);
            font-weight: var(--font-medium);
            box-shadow: var(--shadow-lg);
        }

        &__bar-icon {
            flex-shrink: 0;
            width: var(--spacing-16);
            height: var(--spacing-16);
        }

        &__text {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__cancel {
            flex-shrink: 0;
            border: 0;
            border-radius: var(--rounded-full);
            padding: var(--spacing-6) var(--spacing-14);
            background-color: var(--color-surface);
            color: var(--color-word-1);
            font: inherit;
            cursor: pointer;

            &:focus-visible {
                outline: var(--spacing-2) solid var(--color-surface);
                outline-offset: var(--spacing-2);
            }
        }

        // On the element: the pointer goes through it to the page, where the
        // widget reads what is under it
        &__box {
            position: fixed;
            z-index: 2147483646;
            pointer-events: none;
            border: var(--spacing-2) solid var(--pk-page-pointer-color);
            border-radius: var(--rounded-sm);
            background-color: color-mix(
                in srgb,
                var(--pk-page-pointer-color) 10%,
                transparent
            );
            transition:
                top 0.08s ease-out,
                left 0.08s ease-out,
                width 0.08s ease-out,
                height 0.08s ease-out;
        }

        &__label {
            position: absolute;
            bottom: 100%;
            left: calc(-1 * var(--spacing-2));
            max-width: var(--spacing-320);
            margin-bottom: var(--spacing-4);
            padding: var(--spacing-2) var(--spacing-8);
            overflow: hidden;
            border-radius: var(--rounded-sm);
            background-color: var(--pk-page-pointer-color);
            color: var(--color-white);
            font-family: var(--font-sans);
            font-size: var(--text-12);
            font-weight: var(--font-medium);
            line-height: var(--leading-normal);
            text-overflow: ellipsis;
            white-space: nowrap;

            // An element at the top of the window: the name inside its box
            &--inside {
                top: 0;
                bottom: auto;
                margin: var(--spacing-4);
            }
        }

        &__ask {
            position: fixed;
            z-index: 2147483647;
            display: inline-flex;
            align-items: center;
            gap: var(--spacing-6);
            border: 0;
            border-radius: var(--rounded-full);
            padding: var(--spacing-6) var(--spacing-12);
            background-color: var(--pk-page-pointer-color);
            color: var(--color-white);
            font-family: var(--font-sans);
            font-size: var(--text-14);
            font-weight: var(--font-medium);
            box-shadow: var(--shadow-lg);
            cursor: pointer;

            &:focus-visible {
                outline: var(--spacing-2) solid var(--pk-page-pointer-color);
                outline-offset: var(--spacing-2);
            }
        }

        &__ask-icon {
            width: var(--spacing-16);
            height: var(--spacing-16);
        }
    }

    // In from above, as the page control bar
    .pk-page-pointer-bar-enter-active {
        transition:
            opacity 0.25s ease-out,
            translate 0.35s cubic-bezier(0.32, 0.72, 0, 1);
    }

    .pk-page-pointer-bar-leave-active {
        transition:
            opacity 0.2s ease-in,
            translate 0.2s ease-in;
    }

    .pk-page-pointer-bar-enter-from,
    .pk-page-pointer-bar-leave-to {
        opacity: 0;
        translate: 0 calc(-1 * var(--spacing-16));
    }

    .pk-page-pointer-ask-enter-active,
    .pk-page-pointer-ask-leave-active {
        transition:
            opacity 0.15s ease-out,
            scale 0.15s ease-out;
    }

    .pk-page-pointer-ask-enter-from,
    .pk-page-pointer-ask-leave-to {
        opacity: 0;
        scale: 0.9;
    }

    @media (prefers-reduced-motion: reduce) {
        .pk-page-pointer__box,
        .pk-page-pointer-bar-enter-active,
        .pk-page-pointer-bar-leave-active,
        .pk-page-pointer-ask-enter-active,
        .pk-page-pointer-ask-leave-active {
            transition: none;
        }
    }
</style>
